class GvsDirectoryRail extends HTMLElement {
  #abortController = null;
  #resizeObserver = null;
  #frame = null;

  connectedCallback() {
    this.#initialise();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #initialise() {
    this.#destroy();

    const track = this.querySelector('[data-directory-track]');
    const cards = track ? [...track.querySelectorAll('.gvs-collection-directory__card')] : [];
    const controls = this.querySelector('[data-directory-controls]');
    const previous = this.querySelector('[data-directory-previous]');
    const next = this.querySelector('[data-directory-next]');
    const status = this.querySelector('[data-directory-status]');
    const hasControls = Boolean(controls && previous && next && status);

    if (!track || cards.length < 2) {
      if (track) track.tabIndex = -1;
      controls?.setAttribute('hidden', '');
      this.removeAttribute('data-has-overflow');
      return;
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const trackInset = () => {
      const styles = window.getComputedStyle(track);
      return Number.parseFloat(styles.scrollPaddingInlineStart || styles.paddingInlineStart) || 0;
    };

    const cardLeft = (card) => {
      const trackRect = track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      return cardRect.left - trackRect.left + track.scrollLeft - trackInset();
    };

    const activeIndex = () => {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(cardLeft(card) - track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const update = () => {
      this.#frame = null;
      const overflow = track.scrollWidth - track.clientWidth > 4;
      const index = activeIndex();
      const atStart = track.scrollLeft <= 4;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

      this.toggleAttribute('data-has-overflow', overflow);
      track.tabIndex = overflow ? 0 : -1;

      if (hasControls) {
        controls.hidden = !overflow;
        previous.disabled = !overflow || atStart;
        next.disabled = !overflow || atEnd;
        status.textContent = `${index + 1} of ${cards.length}`;
      }
    };

    const scheduleUpdate = () => {
      if (this.#frame !== null) cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(update);
    };

    const move = (direction) => {
      const current = activeIndex();
      const targetIndex = Math.max(0, Math.min(cards.length - 1, current + direction));
      track.scrollTo({
        left: cardLeft(cards[targetIndex]),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    if (hasControls) {
      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
    }

    track.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      },
      { signal }
    );

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(scheduleUpdate);
      this.#resizeObserver.observe(track);
      cards.forEach((card) => this.#resizeObserver.observe(card));
    }

    scheduleUpdate();
  }

  #destroy() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }
}

if (!customElements.get('gvs-directory-rail')) {
  customElements.define('gvs-directory-rail', GvsDirectoryRail);
}
